import {
  ChevronRight,
  Info,
  LocationOn,
  StorefrontRounded,
  Summarize,
  WcRounded,
} from '@mui/icons-material'
import {
  Box,
  Typography,
  alpha,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
} from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import React, { CSSProperties, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import KumocanButton from 'components/button'
import KumocanDatePicker from 'components/datePicker'
import KumocanDialog from 'components/dialog'
import KumocanSimpleTextareaAutosize from 'components/simpleTextArea'
import KumocanTimePicker from 'components/timePicker'
import WvButton from 'components/wvButton'
import { assignApi } from 'hooks/api/assign'
import { securityApi } from 'hooks/api/security'
import { NewDate } from 'hooks/helper/date'
import { useSnackbar } from 'hooks/useSnackbar/SnackbarContext'
import { ASSIGN_STATUS, AssignWithContract } from 'types/assign'
import { BeginCondition } from 'types/security'
import { PRIMARY_COLOR, WARNING_COLOR } from 'valiable'

import ImageUpload from './components/picture'
import Review from './components/review'

const REFRESH_INTERVAL = 60_000 // 5 minutes
type SecurityNoteHandlingState = 'idle' | 'saving' | 'saved'

export default function AssignPage(): JSX.Element {
  const { t, i18n } = useTranslation(['page-assign', 'common', 'validation'])
  const { showSnackbar } = useSnackbar()

  enum MapType {
    NORMAL,
    WC,
    CONVENIENCE,
  }

  const [assign, setAssign] = useState<AssignWithContract>()
  const [assignDate, setAssignDate] = useState<string>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [isErrorDialogOpen, setErrorDialogOpen] = useState<boolean>(false)
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState<boolean>(false)
  const [condition, setCondition] = useState<number>(BeginCondition.Good)
  const [conditionNote, setConditionNote] = useState<string | undefined>(
    undefined,
  )
  const [securityNoteHandlingState, setSecurityNoteHandlingState] =
    useState<SecurityNoteHandlingState>('idle')
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [savedSecurityNote, setSavedSecurityNote] = useState<string>('')

  type DownForm = {
    downDate: Dayjs | null
    downTime: Dayjs | null
  }

  const { control, setValue, getValues, reset } = useForm<DownForm>()

  const today = dayjs().format('YYYY-MM-DD') // 表示用
  const scleentoday = dayjs().format('YYYY/MM/DD (dd)')
  const [currentTime, setCurrentTime] = useState(dayjs().format('HH:mm'))

  /** 現在のステータスから次のステータスを算出 */
  const getNextStatus = (currentStatus: number) => {
    switch (currentStatus) {
      case ASSIGN_STATUS.AVAILABLE_FOR_WORK:
        return ASSIGN_STATUS.ATTENDANCE_REPORT
      case ASSIGN_STATUS.ATTENDANCE_REPORT:
        return ASSIGN_STATUS.ATTENDANCE_UP
      case ASSIGN_STATUS.ATTENDANCE_UP:
        return ASSIGN_STATUS.ATTENDANCE_DOWN
      case ASSIGN_STATUS.ATTENDANCE_DOWN:
        return ASSIGN_STATUS.ATTENDANCE_DOWN
      default:
        return ASSIGN_STATUS.COMPLETED
    }
  }

  const nextStatus = assign ? getNextStatus(assign.status) : 0

  /** 言語に応じたステータスボタンスタイル */
  const statusButtonStyle: CSSProperties = {
    width: '100%',
    fontSize: i18n.language === 'ja' ? '64px' : '48px',
    fontWeight: 'bold',
    letterSpacing: '0.2em',
    borderRadius: 10,
  }

  /** condition が Bad のときはメモ必須 */
  const canSubmit = (): boolean =>
    (condition === BeginCondition.Good ||
      (condition === BeginCondition.Bad &&
        !!conditionNote &&
        conditionNote.trim() !== '')) &&
    !isSubmitting

  /** データ取得 */
  const fetchData = async () => {
    try {
      const assignResponse = await securityApi().filterAssignByDate(today)
      const endTime = assignResponse.data.contractDetail.endTime
      reset({
        downDate: NewDate(endTime),
        downTime: NewDate(endTime),
      })

      setAssign(assignResponse.data)
      setAssignDate(assignResponse.data.date)
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  /** 画面ロード & 定期更新 */
  useEffect(() => {
    fetchData()

    const timer = setInterval(() => {
      setCurrentTime(dayjs().format('HH:mm'))
    }, REFRESH_INTERVAL)

    const fetchTimer = setInterval(fetchData, 5 * REFRESH_INTERVAL)

    return () => {
      clearInterval(timer)
      clearInterval(fetchTimer)
    }
  }, [today])

  /** 体調 Radio 変更 */
  const handleConditionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setCondition(Number(event.target.value))
  }

  /** 体調メモ */
  const handleConditionNoteInput = (value: string) => {
    setConditionNote(value.trim())
  }

  /** 警備員メモ（サーバに throttle 更新） */
  const throttledInputHandler = useRef(
    throttle(async (id: string, value: string) => {
      setSecurityNoteHandlingState('saving')
      await assignApi().updateSecurityNote(id, value)
      setSecurityNoteHandlingState('saved')
    }, 2000),
  )
  const handleInput = (value: string) => {
    throttledInputHandler.current(assign?.id || '', value.trim())
  }

  const resetConditionMemo = () => {
    setConditionNote('') // 体調メモをリセット
  }

  /** 保存された警備員用メモを確認 */
  const handleConfirmSavedNote = async () => {
    if (!assign?.id) return
    try {
      const response = await assignApi().findById(assign.id)
      setSavedSecurityNote(response.data.securityNote || '')
      setIsConfirmDialogOpen(true)
    } catch (error) {
      console.error('Error fetching saved note:', error)
    }
  }

  const handleConfirmDialogClose = () => {
    setIsConfirmDialogOpen(false)
  }

  /** ダイアログ OPEN/CLOSE */
  const handleOpen = () => setIsDialogOpen(true)
  const handleClose = () => {
    setIsDialogOpen(false)
    setIsReviewDialogOpen(false)
    resetConditionMemo()
    reset({
      downDate: NewDate(assign?.contractDetail.endTime),
      downTime: NewDate(assign?.contractDetail.endTime),
    })
  }
  const handleErrorDialogClose = () => setErrorDialogOpen(false)

  /** status 更新 */
  const submit = () => {
    if (assign && !isSubmitting) {
      setIsSubmitting(true)
      assignApi()
        .updateStatus(assign.id, nextStatus, condition, conditionNote)
        .then(() => {
          setIsDialogOpen(false)
          setAssign({ ...assign, status: nextStatus })
          if (nextStatus === ASSIGN_STATUS.ATTENDANCE_DOWN) {
            setIsReviewDialogOpen(true)
          }
        })
        .finally(() => {
          setIsSubmitting(false)
        })
    }
  }

  /** 下番報告 */
  const down = () => {
    const date = getValues('downDate')
    const time = getValues('downTime')
    if (!(assign && date && time)) return

    const endTime = date
      .hour(time.hour())
      .minute(time.minute())
      .second(0)
      .millisecond(0)

    assignApi()
      .down(assign.id, endTime)
      .then(() => {
        setAssign({ ...assign, status: ASSIGN_STATUS.ATTENDANCE_DOWN })
        setIsDialogOpen(false)
        setIsReviewDialogOpen(true)
      })
      .catch((error) => {
        if (error.data.code === 400 && error.data.errorCode === 400) {
          setIsDialogOpen(false)
          setErrorDialogOpen(true)
        }
      })
  }

  /** 下番ピッカーを現在時刻に */
  const setCurrentDateTime = () => {
    const now = dayjs()
    setValue('downDate', now)
    setValue('downTime', now)
  }

  /** 警備報告書へ */
  const toSecurityReport = () => {
    location.href = `/security-report?contract_detail_id=${assign?.contractDetail.id}&date=${assignDate}&slide=right`
  }

  /** 詳細ページへ */
  const toDetail = () => {
    if (!assign?.id) return
    location.href = `/assign/detail?id=${assign.id}&slide=right`
  }

  /** Google Map リンク生成 */
  const googleMapLink = (type: MapType) => {
    if (!assign) return ''
    const { lat, lng, address } = assign
    let query = ''
    let specificLink = false

    switch (type) {
      case MapType.WC:
        query = 'トイレ'
        specificLink = true
        break
      case MapType.CONVENIENCE:
        query = 'コンビニ'
        specificLink = true
        break
      case MapType.NORMAL:
      default:
        query = ''
        break
    }

    if (!address) {
      showSnackbar(t('common:messages.address_not_registered'))
    } else if (lat && lng && lat !== 0 && lng !== 0) {
      if (specificLink) {
        location.href = `https://www.google.com/maps/search/${query}/@${lat},${lng},17z?launch_app=true`
      } else {
        location.href = `https://www.google.com/maps/search/?api=1&query=${query} ${lat},${lng}&launch_app=true`
      }
    } else {
      location.href = `https://www.google.com/maps/search/?api=1&query=${query} ${encodeURIComponent(address)}&launch_app=true`
    }
  }

  const handleButtonClick = () => {
    handleOpen()
  }

  const isBeginConditionRequired =
    assign?.useSecurityBeginCondition &&
    nextStatus === ASSIGN_STATUS.ATTENDANCE_REPORT

  // 体調報告あり
  const conditionReportSubmit = (
    <Box>
      <Typography sx={conditionReportTitleStyle}>
        {t('departure_report.title')}
      </Typography>

      <Box sx={{ borderBottom: '1px solid #ccc', mb: 1 }} />

      <Typography sx={conditionReportDescriptionStyle}>
        {t('condition_report.prompt')}
      </Typography>

      <RadioGroup
        name="beginCondition"
        value={condition}
        onChange={handleConditionChange}
      >
        <FormControlLabel
          value={BeginCondition.Good}
          onChange={resetConditionMemo}
          control={
            <Radio sx={{ '& .MuiSvgIcon-root': { fontSize: '1.5rem' } }} />
          }
          label={
            <Typography sx={conditionLabelTextStyle}>
              {t('condition_report.status.healthy')}
            </Typography>
          }
          sx={conditionLabelContainerStyle}
        />
        <FormControlLabel
          value={BeginCondition.Bad}
          control={
            <Radio sx={{ '& .MuiSvgIcon-root': { fontSize: '1.5rem' } }} />
          }
          label={
            <Typography sx={conditionLabelTextStyle}>
              {t('condition_report.status.sick')}
            </Typography>
          }
          sx={conditionLabelContainerStyle}
        />
      </RadioGroup>

      {condition === BeginCondition.Bad && (
        <>
          <Typography sx={conditionBadNoteDescriptionStyle}>
            {t('condition_report.sick_note.prompt')}
          </Typography>
          <KumocanSimpleTextareaAutosize
            id="begin_condition_note"
            onInput={handleConditionNoteInput}
            placeHolder={t('condition_report.sick_note.placeholder') ?? ''}
            required={condition === BeginCondition.Bad}
            style={{
              width: '96%',
              height: '100px',
              fontSize: '14px',
              border: `1px solid ${canSubmit() ? 'black' : 'red'}`,
              borderRadius: '4px',
            }}
          />
        </>
      )}

      <Box sx={conditionReportButtonContainerStyle}>
        <KumocanButton
          onClick={handleClose}
          variant="outlined"
          style={conditionReportCancelButtonStyle}
        >
          {t('common:buttons.cancel')}
        </KumocanButton>
        <KumocanButton
          disabled={!canSubmit()}
          onClick={submit}
          style={conditionReportSubmitButtonStyle}
        >
          {t('departure_report.submit')}
        </KumocanButton>
      </Box>
    </Box>
  )

  // 単純 OK/キャンセル
  const simpleReportSubmit = (
    <Box sx={simpleReportContainerStyle}>
      <Typography sx={simpleReportTextStyle}>
        {t('common:messages.confirm_update')}
      </Typography>
      <Box sx={simpleReportButtonContainerStyle}>
        <KumocanButton
          onClick={handleClose}
          variant="outlined"
          style={simpleReportCancelButtonStyle}
        >
          {t('common:buttons.cancel')}
        </KumocanButton>
        <KumocanButton
          disabled={isSubmitting}
          onClick={submit}
          style={simpleReportOkButtonStyle}
        >
          {t('common:buttons.ok')}
        </KumocanButton>
      </Box>
    </Box>
  )

  // 下番報告
  const downReportSubmit = (
    <Box sx={downReportContainerStyle}>
      <Typography sx={downReportTitleStyle}>
        {t('down_report.title')}
      </Typography>
      <Typography sx={downReportDescriptionStyle}>
        {t('down_report.description')}
      </Typography>
      <Box sx={downDatePickerContainerStyle}>
        <KumocanDatePicker
          format="MM/DD (dd)"
          name="downDate"
          control={control}
          style={datepickerStyle}
        />
        <KumocanTimePicker
          name="downTime"
          control={control}
          style={datepickerStyle}
        />
      </Box>
      <KumocanButton
        onClick={setCurrentDateTime}
        variant="outlined"
        style={downCurrentTimeButtonStyle}
        color="inherit"
      >
        {t('down_report.set_current_time')}
      </KumocanButton>
      <KumocanButton
        disabled={isSubmitting}
        onClick={down}
        style={downSubmitButtonStyle}
      >
        {t('down_report.submit')}
      </KumocanButton>
      <KumocanButton
        onClick={handleClose}
        variant="outlined"
        style={downCancelButtonStyle}
      >
        {t('common:buttons.cancel')}
      </KumocanButton>
    </Box>
  )

  /** ---------------- UI ---------------- */

  return (
    <Box sx={mainContainerStyle}>
      <Box sx={dateTimeWrapperStyle}>
        {/* 日付 & 現在時刻 */}
        <Box sx={dateTimeContainerStyle}>
          <Typography component="span" sx={dateTextStyle}>
            {scleentoday}
          </Typography>
          <Typography component="span" sx={timeTextStyle}>
            {currentTime}
          </Typography>
        </Box>

        {/* 案件なし */}
        {!assign?.id ? (
          <Box sx={noAssignBoxStyle}>
            <Typography sx={noAssignTitleStyle}>
              {t('no_assignment')}
            </Typography>
            <Typography sx={noAssignDescriptionStyle}>
              {t('no_assignment_description')}
            </Typography>
          </Box>
        ) : (
          <>
            {/* 状態ボタン */}
            <WvButton
              type="secondary"
              style={statusButtonStyle}
              onClick={handleButtonClick}
              status={assign?.status}
            />

            {/* 写真アップロード */}
            <ImageUpload
              assignId={assign.id}
              hasSecurityReport={assign.hasSecurityReport}
            />

            <Box sx={assignInfoContainerStyle}>
              {/* 案件情報 */}
              <Box sx={assignDetailBoxStyle}>
                <Typography component="span" sx={assignNameStyle}>
                  {assign?.contractDetail.name}
                  {assign?.loanInfo?.borrowCompanyId && (
                    <>
                      {'　'}
                      {assign.loanInfo.borrowCompanyName}/
                      {assign.loanInfo.borrowCompanyBranchName}
                    </>
                  )}
                </Typography>
                <Typography component="span" sx={assignTimeStyle}>
                  {assign?.contractDetail.startTime.format('H:mm')}~
                  {assign?.contractDetail.endTime.format('H:mm')}
                </Typography>
              </Box>

              {/* 地図 & 詳細 */}
              <Box sx={mapDetailButtonContainerStyle}>
                <WvButton
                  type="alert"
                  variant="outlined"
                  style={mapButtonStyle}
                  onClick={() => googleMapLink(MapType.NORMAL)}
                >
                  <LocationOn />
                  <span style={mapButtonTextStyle}>
                    {t('common:buttons.map')}
                  </span>
                </WvButton>

                <Button
                  variant="outlined"
                  style={detailButtonStyle}
                  onClick={toDetail}
                >
                  <span style={detailButtonTextStyle}>
                    {t('common:buttons.detail')}
                  </span>
                  <ChevronRight />
                </Button>
              </Box>

              {/* トイレ & コンビニ */}
              <Box sx={facilityButtonContainerStyle}>
                <WvButton
                  type="secondary"
                  style={facilityButtonStyle}
                  onClick={() => googleMapLink(MapType.WC)}
                >
                  <WcRounded />
                  <span style={facilityButtonTextStyle}>
                    {t('common:facilities.toilet')}
                  </span>
                </WvButton>
                <WvButton
                  type="secondary"
                  style={facilityButtonStyle}
                  onClick={() => googleMapLink(MapType.CONVENIENCE)}
                >
                  <StorefrontRounded />
                  <span style={facilityButtonTextStyle}>
                    {t('common:facilities.convenience_store')}
                  </span>
                </WvButton>
              </Box>

              {/* 同じ現場の履歴を確認する */}
              <Box sx={historyLinkContainerStyle}>
                <Typography
                  component="a"
                  onClick={() => {
                    location.href = `/assign/history?contract_detail_id=${assign?.contractDetail.id}&name=${encodeURIComponent(assign?.contractDetail.name || '')}&slide=right`
                  }}
                  sx={historyLinkTextStyle}
                >
                  {t('history_link')}
                </Typography>
              </Box>
            </Box>

            {/* 警備報告書ボタン */}
            {assign?.isLeader && (
              <Box style={securityReportButtonStyle}>
                <WvButton
                  type="securityReport"
                  style={securityReportInnerButtonStyle}
                  onClick={toSecurityReport}
                >
                  <Summarize />
                  {t('security_report.title')}
                </WvButton>
              </Box>
            )}

            {/* メモ */}
            <Box sx={memoContainerStyle}>
              <KumocanSimpleTextareaAutosize
                id="text-area"
                onInput={handleInput}
                placeHolder={t('security_report.note') ?? ''}
                defaultValue={assign?.securityNote}
                style={memoTextAreaStyle}
              />
              <Box sx={memoInfoContainerStyle}>
                <Info sx={memoInfoIconStyle} />
                <Typography sx={memoInfoTextStyle}>
                  {t(`security_note.status.${securityNoteHandlingState}`)}
                </Typography>
              </Box>
              <Box sx={savedMemoLinkContainerStyle}>
                <Typography
                  component="span"
                  onClick={handleConfirmSavedNote}
                  sx={savedMemoLinkTextStyle}
                >
                  {t('security_note.check_saved')}
                </Typography>
              </Box>
            </Box>

            {/* 保存内容確認ダイアログ */}
            <KumocanDialog
              open={isConfirmDialogOpen}
              handleClose={handleConfirmDialogClose}
            >
              <Box sx={savedMemoDialogContainerStyle}>
                <Typography sx={savedMemoDialogTitleStyle}>
                  {t('security_note.saved_title')}
                </Typography>
                <Box sx={savedMemoContentBoxStyle}>
                  <Typography sx={savedMemoTextStyle}>
                    {savedSecurityNote || t('security_note.no_saved')}
                  </Typography>
                </Box>
                <Box sx={savedMemoButtonContainerStyle}>
                  <KumocanButton
                    onClick={handleConfirmDialogClose}
                    style={savedMemoCloseButtonStyle}
                  >
                    {t('common:buttons.close')}
                  </KumocanButton>
                </Box>
              </Box>
            </KumocanDialog>

            {/* ダイアログ */}
            <KumocanDialog open={isDialogOpen} handleClose={handleClose}>
              {isBeginConditionRequired
                ? conditionReportSubmit
                : nextStatus === ASSIGN_STATUS.ATTENDANCE_DOWN
                  ? downReportSubmit
                  : simpleReportSubmit}
            </KumocanDialog>

            {/* 報告書未提出エラー */}
            <KumocanDialog
              open={isErrorDialogOpen}
              handleClose={handleErrorDialogClose}
            >
              <Box>
                <Typography sx={errorDialogTextStyle}>
                  {t('validation:required', {
                    field: t('security_report.title'),
                  })}
                </Typography>
              </Box>
            </KumocanDialog>

            {/* レビュー */}
            <Review
              id={assign.id}
              open={isReviewDialogOpen}
              setOpen={setIsReviewDialogOpen}
            />
          </>
        )}
      </Box>
    </Box>
  )
}

/** throttle helper */
function throttle(func: (...args: any[]) => void, limit: number) {
  let lastFunc: NodeJS.Timeout
  let lastRan: number

  return function (...args: any[]) {
    if (!lastRan) {
      func(...args)
      lastRan = Date.now()
    } else {
      clearTimeout(lastFunc)
      lastFunc = setTimeout(
        () => {
          if (Date.now() - lastRan >= limit) {
            func(...args)
            lastRan = Date.now()
          }
        },
        limit - (Date.now() - lastRan),
      )
    }
  }
}

// 体調報告タイトル
const conditionReportTitleStyle = {
  fontSize: '24px',
  fontWeight: 'bold',
  textAlign: 'center',
  mb: 2,
}

// 体調報告説明
const conditionReportDescriptionStyle = {
  fontSize: '16px',
  fontWeight: 'bold',
  textAlign: 'center',
}

// 体調ラベルテキスト（良い/悪い共通）
const conditionLabelTextStyle = {
  fontSize: '18px',
  fontWeight: 'bold',
  color: '#14b7ae',
}

// 体調ラベルコンテナ（良い/悪い共通）
const conditionLabelContainerStyle = {
  m: '4px 0',
  border: '1px solid #14b7ae',
  borderRadius: '8px',
  p: '8px',
}

// 体調悪い説明
const conditionBadNoteDescriptionStyle = {
  fontSize: '14px',
  textAlign: 'center',
}

// 体調報告ボタンコンテナ
const conditionReportButtonContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  mt: 2,
}

// 体調報告キャンセルボタン
const conditionReportCancelButtonStyle: CSSProperties = {
  minWidth: '100px',
  height: '50px',
  fontSize: '18px',
}

// 体調報告送信ボタン
const conditionReportSubmitButtonStyle: CSSProperties = {
  minWidth: '100px',
  height: '50px',
  fontSize: '18px',
}

// 単純報告コンテナ
const simpleReportContainerStyle = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
}

// 単純報告テキスト
const simpleReportTextStyle = {
  fontSize: '20px',
  fontWeight: 'bold',
}

// 単純報告ボタンコンテナ
const simpleReportButtonContainerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
}

// 単純報告キャンセルボタン
const simpleReportCancelButtonStyle: CSSProperties = {
  minWidth: '100px',
  height: '50px',
  fontSize: '18px',
}

// 単純報告OKボタン
const simpleReportOkButtonStyle: CSSProperties = {
  minWidth: '100px',
  height: '50px',
  fontSize: '18px',
}

// 下番報告コンテナ
const downReportContainerStyle = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  textAlign: 'center',
}

// 下番報告タイトル
const downReportTitleStyle = {
  fontSize: '22px',
  fontWeight: 'bold',
}

// 下番報告説明
const downReportDescriptionStyle = {
  fontSize: '20px',
}

// 下番DatePickerコンテナ
const downDatePickerContainerStyle = {
  display: 'flex',
  gap: 1,
}

const datepickerStyle = {
  width: '50%',
  '& .MuiInputBase-input': {
    fontSize: '25px',
    textAlign: 'center',
    lineHeight: '56px',
  },
  '& .MuiInputBase-root': {
    height: '50px',
  },
}

// 下番現在時刻ボタン
const downCurrentTimeButtonStyle: CSSProperties = {
  minWidth: '100px',
  minHeight: '40px',
  fontSize: '14px',
  fontWeight: 'bold',
}

// 下番送信ボタン
const downSubmitButtonStyle: CSSProperties = {
  minWidth: '100px',
  minHeight: '70px',
  fontSize: '40px',
  fontWeight: 'bold',
}

// 下番キャンセルボタン
const downCancelButtonStyle: CSSProperties = {
  minWidth: '100px',
  minHeight: '40px',
  fontSize: '16px',
}

// メインコンテナ
const mainContainerStyle = {
  width: '95%',
  m: '10px auto 100px',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
}

// 日付時刻ラッパー
const dateTimeWrapperStyle = {
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 3,
  p: '20px 0',
  borderRadius: '8px',
}

// 日付時刻コンテナ
const dateTimeContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}

// 日付テキスト
const dateTextStyle = {
  fontSize: '20px',
  fontWeight: 'bold',
  color: '#333',
}

// 時刻テキスト
const timeTextStyle = {
  fontSize: '84px',
  fontWeight: 'bold',
  color: '#333',
  letterSpacing: '0.05em',
}

// 案件なしBox
const noAssignBoxStyle = {
  backgroundColor: '#ffffff',
  p: '20px',
  borderRadius: '12px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  textAlign: 'center',
}

// 案件なしタイトル
const noAssignTitleStyle = {
  fontSize: '24px',
  fontWeight: 'bold',
  color: '#757575',
}

// 案件なし説明
const noAssignDescriptionStyle = {
  fontSize: '14px',
  color: '#999',
  mt: 1,
}

// 案件情報コンテナ
const assignInfoContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  borderRadius: '8px',
  marginTop: '20px',
  background: alpha(PRIMARY_COLOR, 0.1),
  padding: '16px',
}

// 案件詳細Box
const assignDetailBoxStyle = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  p: '10px 0',
}

// 案件名
const assignNameStyle = {
  fontSize: '24px',
  color: PRIMARY_COLOR,
}

// 案件時間
const assignTimeStyle = {
  fontSize: '18px',
  color: '#757575',
}

// 地図詳細ボタンコンテナ
const mapDetailButtonContainerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '10px',
}

const mapButtonStyle: CSSProperties = {
  minWidth: '60px',
  height: '60px',
  flexDirection: 'column',
  color: WARNING_COLOR,
  padding: '4px',
}

const mapButtonTextStyle: CSSProperties = {
  fontSize: 12,
}

const detailButtonStyle: CSSProperties = {
  width: '100%',
  height: '60px',
  borderColor: PRIMARY_COLOR,
  color: PRIMARY_COLOR,
  backgroundColor: '#FFFFFF',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}

const detailButtonTextStyle: CSSProperties = {
  fontSize: 18,
  fontWeight: 'bold',
  flex: 1,
}

// 施設ボタンコンテナ
const facilityButtonContainerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '10px',
}

const facilityButtonStyle: CSSProperties = {
  flex: 1,
  height: '60px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}

const facilityButtonTextStyle: CSSProperties = {
  fontSize: 18,
}

// 履歴リンクコンテナ
const historyLinkContainerStyle = {
  textAlign: 'right',
  marginTop: '16px',
}

// 履歴リンクテキスト
const historyLinkTextStyle = {
  fontSize: '14px',
  color: '#666',
  cursor: 'pointer',
  textDecoration: 'underline',
  display: 'inline-block',
}

const securityReportButtonStyle: CSSProperties = {
  marginTop: '20px',
}

// 警備報告書ボタン内側
const securityReportInnerButtonStyle: CSSProperties = {
  width: '100%',
  height: '60px',
  fontSize: '20px',
  fontWeight: 'bold',
}

// メモコンテナ
const memoContainerStyle = {
  marginTop: '20px',
  width: '98%',
}

// メモTextArea
const memoTextAreaStyle: CSSProperties = {
  width: '100%',
  height: '100px',
  fontSize: '20px',
}

// メモ情報コンテナ
const memoInfoContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  marginTop: '8px',
  padding: '4px 0',
}

// メモ情報アイコン
const memoInfoIconStyle = {
  fontSize: '20px',
  color: '#666',
}

// メモ情報テキスト
const memoInfoTextStyle = {
  fontSize: '14px',
  color: '#666',
}

// 保存メモリンクコンテナ
const savedMemoLinkContainerStyle = {
  textAlign: 'right',
  marginTop: '4px',
}

// 保存メモリンクテキスト
const savedMemoLinkTextStyle = {
  fontSize: '14px',
  color: '#666',
  cursor: 'pointer',
  padding: '4px 0',
  display: 'inline-block',
  textDecoration: 'underline',
}

// 保存メモダイアログコンテナ
const savedMemoDialogContainerStyle = {
  width: '100%',
}

// 保存メモダイアログタイトル
const savedMemoDialogTitleStyle = {
  fontSize: '20px',
  fontWeight: 'bold',
  mb: 2,
}

// 保存メモコンテンツBox
const savedMemoContentBoxStyle = {
  backgroundColor: '#f5f5f5',
  padding: '16px',
  borderRadius: '8px',
  minHeight: '100px',
  maxHeight: '300px',
  overflowY: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}

// 保存メモテキスト
const savedMemoTextStyle = {
  fontSize: '16px',
}

// 保存メモボタンコンテナ
const savedMemoButtonContainerStyle = {
  mt: 2,
  display: 'flex',
  justifyContent: 'center',
}

// 保存メモ閉じるボタン
const savedMemoCloseButtonStyle: CSSProperties = {
  minWidth: '100px',
  height: '40px',
  fontSize: '16px',
}

// エラーダイアログテキスト
const errorDialogTextStyle = {
  fontSize: '20px',
  fontWeight: 'bold',
}
